import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  shape, string, useDataModel, params, arrayOf, number, bool
} from '@thd-nucleus/data-sources';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { ResultsApplied } from '../components/ResultsApplied';
import { shouldDisplayResultsAppliedInner, shouldDisplayResultsAppliedOuter } from '../utils/searchHelpers';
import { ProductResultsSearchMethodsPLP } from '../utils/product-results-search-methods-PLP';
import { ProductResultsSearchMethodsPEP } from '../utils/product-results-search-methods-PEP';
import { usePlpResultsHeaderReadyEvent } from '../hooks/usePlpResultsHeaderReadyEvent';

export const ResultsAppliedComposable = ({
  propsToUse = {},
  renderingConditions = {}
}) => {
  const ctx = useContext(ExperienceContext);
  const { customContext } = ctx || {};
  const { variables = {}, pageType } = customContext || {};
  const scrollTargetRef = usePlpResultsHeaderReadyEvent();
  const defaultOptions = {
    variables,
    ssr: true,
    skip: !variables.navParam && !variables.keyword,
  };
  const {
    data
  } = useDataModel('searchModel', defaultOptions);
  const { metadata } = data?.searchModel || {};
  const { contentType } = metadata || {};
  const isCategory = contentType === 'categorypage';

  const viewPrimaryFilters = useConfigService('fs-prop:isviewPrimaryFiltersEnabled');
  const isPlpSpeedEnabled = useConfigService('fs-prop:isPlpSpeedEnabled');
  // TODO: Refactor consumer components to remove the dead code
  // these have been enabled long enough, making the fallback code unreachable
  const isGetItFastOnPlp = true;
  const isFulfillmentRedesignEnabled = true;

  const isGetItFastOrIsFulfillment = isGetItFastOnPlp || isFulfillmentRedesignEnabled;

  const { onDimensionsChangePLP } = ProductResultsSearchMethodsPLP();
  const { onDimensionsChangePEP } = ProductResultsSearchMethodsPEP();

  const areAppliedRefinementsAvailable = (!!data?.searchModel?.appliedDimensions
    && data.searchModel.appliedDimensions.length > 0);

  const onDimensionsChange = useCallback(({ refinement }) => {
    if (pageType === 'pep') {
      return onDimensionsChangePEP({ refinement, scrollTargetRef });
    }
    return onDimensionsChangePLP({ refinement, viewPrimaryFilters });
  }, [onDimensionsChangePLP, onDimensionsChangePEP, viewPrimaryFilters]);

  // Build props object based on propsToUse object
  const availableProps = {
    onAppliedChange: onDimensionsChange,
    isFulfillmentRedesignEnabled: !!isFulfillmentRedesignEnabled,
    isGetItFastOnPlp: !!isGetItFastOnPlp,
    viewPrimaryFilters: !!viewPrimaryFilters,
    hideResultsCount: !!viewPrimaryFilters
  };
  const dynamicProps = useMemo(() => {
    const props = {};
    Object.entries(propsToUse).forEach(([propName, shouldInclude]) => {
      if (shouldInclude && propName in availableProps) {
        props[propName] = availableProps[propName];
      }
    });
    return props;
  }, [availableProps, propsToUse]);

  if (isCategory) {
    return null;
  }

  // Helper function to get actual condition values
  const getConditionValue = (conditionName) => {
    switch (conditionName) {
    case 'shouldDisplayResultsAppliedOuter':
      return shouldDisplayResultsAppliedOuter(ctx);
    case 'shouldDisplayResultsAppliedInner':
      return shouldDisplayResultsAppliedInner(ctx);
    case 'viewPrimaryFilters':
      return viewPrimaryFilters;
    case 'isGetItFastOnPlp':
      return isGetItFastOnPlp;
    case 'areAppliedRefinementsAvailable':
      // Temporary (PLP speed A/B test):
      // Bypass when PLP speed is enabled so ResultsApplied always renders,
      // allowing the results count to show even without applied refinements.
      // Remove after A/B test. If successful, reconfigure Fusion slots directly.
      return isPlpSpeedEnabled || areAppliedRefinementsAvailable;
    case 'isGetItFastOrIsFulfillment':
      return isGetItFastOrIsFulfillment;
    default:
      return true;
    }
  };

  // Check rendering conditions
  const hasRenderingConditions = Object.keys(renderingConditions).length > 0;
  if (hasRenderingConditions) {
    const shouldRender = Object.entries(renderingConditions).every(([conditionName, expectedValue]) => {
      const actualValue = getConditionValue(conditionName);
      return actualValue === expectedValue;
    });

    if (!shouldRender) {
      return null;
    }
  }

  return (
    <ResultsApplied
      data={data}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...dynamicProps}
    />
  );
};

ResultsAppliedComposable.defaultProps = {
  propsToUse: {},
  renderingConditions: {}
};

ResultsAppliedComposable.propTypes = {
  propsToUse: PropTypes.shape({
    onAppliedChange: PropTypes.bool,
    isFulfillmentRedesignEnabled: PropTypes.bool,
    isGetItFastOnPlp: PropTypes.bool,
    viewPrimaryFilters: PropTypes.bool,
    hideResultsCount: PropTypes.bool
  }),
  renderingConditions: PropTypes.shape({
    shouldDisplayResultsAppliedOuter: PropTypes.bool,
    shouldDisplayResultsAppliedInner: PropTypes.bool,
    viewPrimaryFilters: PropTypes.bool,
    isGetItFastOnPlp: PropTypes.bool,
    areAppliedRefinementsAvailable: PropTypes.bool,
    isGetItFastOrIsFulfillment: PropTypes.bool
  })
};

ResultsAppliedComposable.dataModel = {
  searchModel: params({ keyword: string(), navParam: string() }).shape({
    metadata: shape({
      contentType: string(),
      canonicalUrl: string(),
      clearAllRefinementsURL: string(),
    }),
    searchReport: shape({
      keyword: string(),
      sortBy: string(),
      sortOrder: string(),
      totalProducts: number(),
    }),
    appliedDimensions: arrayOf(
      shape({
        label: string(),
        isNumericFilter: bool(),
        refinements: arrayOf(
          shape({
            label: string(),
            refinementKey: string(),
          })
        ),
      })
    ),
  }),
};

ResultsAppliedComposable.displayName = 'ResultsAppliedComposable';
